<script lang="ts" setup>
import { useResizeObserver } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import {
  computed,
  onMounted,
  ref,
  toRefs,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { GrammarQuaternaryButton } from '@nora-monorepo/grammar-ui';
import type { QuotedMessageResultDTO } from '@nora-monorepo/nora-api-client';
import { formatName } from '@nora-monorepo/shared';
import {
  bgBlue,
  bgOrange,
  bgPrimary,
  bgRed,
  bgSecondary,
  borderBlue,
  HoverableUserName,
  textPrimary,
  textRed,
  textSecondary,
  textTertiary,
} from '@nora-monorepo/ui-shared';

import MessageAttachments from '#nora-chat-ui/components/open-conversations/messages/message-attachments/MessageAttachments.vue';
import MessageForward from '#nora-chat-ui/components/open-conversations/messages/message-forward/MessageForward.vue';
import MessageForwardContent from '#nora-chat-ui/components/open-conversations/messages/message-forward/MessageForwardContent.vue';
import MessageQuoteContent from '#nora-chat-ui/components/open-conversations/messages/message-quote/MessageQuoteContent.vue';
import MessageReactions from '#nora-chat-ui/components/open-conversations/messages/message-reactions/MessageReactions.vue';
import MessageContent from '#nora-chat-ui/components/open-conversations/messages/MessageContent.vue';
import MessageInteractionHandler from '#nora-chat-ui/components/open-conversations/messages/MessageInteractionHandler.vue';
import SeenBy from '#nora-chat-ui/components/open-conversations/messages/seen-by/SeenBy.vue';
import SeenByModal from '#nora-chat-ui/components/open-conversations/messages/seen-by/SeenByModal.vue';
import type { MessageState } from '#nora-chat-ui/states';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { useConversationMessageStore } from '#nora-chat-ui/stores/conversation/message/conversation-message-store';
import { useSeenByModalStore } from '#nora-chat-ui/stores/conversation/message/seen-by-modal-store';
import { useChatUserStore } from '#nora-chat-ui/stores/user-store';

const props = defineProps<ViewMessageProps>();

const { d, t } = useI18n();

interface ViewMessageProps {
  messageState: MessageState;
  containerRef: HTMLElement | null;
  isNewest: boolean;
  operationId?: string | null;
}

const { messageState } = toRefs(props);

const userStore = useChatUserStore();
const conversationStore = useConversationStore();
const conversationMessageStore = useConversationMessageStore();
const seenByModalStore = useSeenByModalStore();
const showFullDate = ref(false);
const isInView = ref(false);
const isLongMessage = ref(false);
const isMessageExpanded = ref(false);
const messageRowRef = ref<HTMLElement | null>(null);
const messageContainerRef = ref<HTMLElement | null>(null);
const messageContentRef = ref<HTMLElement | null>(null);
let highlightTimer: ReturnType<typeof setTimeout> | null = null;
const HIGHLIGHT_TIMEOUT_MS = 5000;
const COLLAPSED_MESSAGE_HEIGHT_PX = 240;

const { getLastSentMessage, getLastReceivedMessage }
  = storeToRefs(conversationStore);

const isMyMessage = computed(
  () => messageState.value.message.sentBy.id === userStore.user?.id,
);

const isBeingEdited = computed(() => {
  const messageEditId
    = conversationMessageStore.editingMessage?.oldMessage.message.id;
  return messageEditId === messageState.value.message.id;
});

useResizeObserver(messageContentRef, ([entry]) => {
  isLongMessage.value
    = entry.contentRect.height > COLLAPSED_MESSAGE_HEIGHT_PX;

  if (!isLongMessage.value) {
    isMessageExpanded.value = false;
  }
});

watch(
  () => [
    messageState.value.message.id,
    messageState.value.message.message,
  ],
  () => {
    isMessageExpanded.value = false;
  },
);

function toggleMessageExpanded() {
  isMessageExpanded.value = !isMessageExpanded.value;
}

const isHighlighted = computed(() => {
  const conversationState = conversationStore.getConversation(
    messageState.value.message.conversationId,
  );
  return conversationState?.highlightedMessage?.id === messageState.value.message.id;
});

function clearHighlightAfterTimer() {
  if (highlightTimer) {
    clearTimeout(highlightTimer);
  }
  highlightTimer = setTimeout(() => {
    const conversationState = conversationStore.getConversation(
      messageState.value.message.conversationId,
    );
    const isCurrentMessageHighlighted = conversationState?.highlightedMessage?.id === messageState.value.message.id;

    if (isCurrentMessageHighlighted && conversationState) {
      conversationState.highlightedMessage = undefined;
    }
  }, HIGHLIGHT_TIMEOUT_MS);
}

watch(isHighlighted, (newValue) => {
  if (newValue) {
    clearHighlightAfterTimer();
  }
});

const MIN_REACTIONS = 2;
const MAX_REACTIONS_NO_MESSAGE = 4;
const MAX_REACTIONS = 15;
const APPROXIMATE_REACTION_WIDTH = 22;

const maxReactions = computed(() => {
  if (!messageContainerRef.value) {
    return MAX_REACTIONS_NO_MESSAGE;
  }

  const width = messageContainerRef.value.clientWidth;
  const widthReactions = Math.floor(width / APPROXIMATE_REACTION_WIDTH);
  const maxReactions = Math.max(MIN_REACTIONS, widthReactions);

  return Math.min(maxReactions, MAX_REACTIONS);
});

const isMyLastSentMessage = computed(
  () =>
    getLastSentMessage.value(
      messageState.value.message.conversationId,
      userStore.user?.id ?? '',
    )?.message.id === messageState.value.message.id,
);

const isMyLastReceivedMessage = computed(
  () =>
    getLastReceivedMessage.value(
      messageState.value.message.conversationId,
      userStore.user?.id ?? '',
    )?.message.id === messageState.value.message.id,
);

function handleIntersection(entries: IntersectionObserverEntry[]) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      conversationMessageStore.setMessageIsInView(messageState.value, true);

      isInView.value = true;
    }
    else {
      conversationMessageStore.setMessageIsInView(messageState.value, false);

      isInView.value = false;
    }
  });
}

let intersectionObserverInitialized = false;

watch(() => props.containerRef, setupIntersectionObserver, {
  immediate: true,
});

function toggleSeenByModal(state: boolean) {
  if (state) {
    seenByModalStore.openModal(
      messageState.value.message.id,
      messageState.value.message.conversationId,
    );
  }
  else {
    seenByModalStore.reset();
  }
}

function toggleShowFullDate() {
  showFullDate.value = !showFullDate.value;
}

const showDate = computed(() => {
  const conversation = conversationStore.getConversation(
    messageState.value.message.conversationId,
  );

  const messageIndex
    = conversation?.messages.findIndex(
      message => message.message.id === messageState.value.message.id,
    ) ?? 0;

  if (messageIndex === 0) {
    return true;
  }

  const previousMessage = conversation?.messages[messageIndex - 1];

  if (!previousMessage) {
    return true;
  }

  return (
    d(messageState.value.message.createdAt)
    !== d(previousMessage.message.createdAt)
  );
});

function handleQuotedMessageClick(message?: QuotedMessageResultDTO | null) {
  if (!message) {
    return;
  }

  const conversationState = conversationStore.getConversation(
    messageState.value.message.conversationId,
  );

  if (conversationState) {
    conversationState.highlightedMessage = message;
  }
}

watch(messageRowRef, setupIntersectionObserver, {
  immediate: true,
});

function setupIntersectionObserver() {
  if (intersectionObserverInitialized) {
    return;
  }
  if (isMyMessage.value) {
    return;
  }
  if (!messageRowRef.value) {
    return;
  }
  if (!props.containerRef) {
    return;
  }

  const options = {
    root: props.containerRef,
    rootMargin: '0px',
    threshold: 0,
  };

  const observer = new IntersectionObserver(handleIntersection, options);
  observer.observe(messageRowRef.value);

  intersectionObserverInitialized = true;
}

onMounted(() => {
  if (isHighlighted.value) {
    clearHighlightAfterTimer();
  }
});
</script>

<template>
  <div v-if="showFullDate">
    <p class="text-center p-2 text-sm" :class="[textSecondary]">
      {{ d(messageState.message.createdAt, 'long') }}
    </p>
  </div>
  <div v-else-if="showDate">
    <p class="text-center p-2 text-sm" :class="[textSecondary]">
      {{ d(messageState.message.createdAt, 'date') }}
    </p>
  </div>

  <div
    ref="messageRowRef"
    class="py-1.5 px-3 flex w-full"
    :class="[{ 'flex-row-reverse': isMyMessage }]"
    data-name="conversation-message"
  >
    <div
      class="flex flex-row gap-1 items-center w-full"
      :class="[
        {
          'flex-row-reverse': isMyMessage,
        },
      ]"
    >
      <div class="flex flex-col flex-wrap max-w-[calc(100%-72px)] min-w-0">
        <MessageInteractionHandler
          :max-reactions="maxReactions"
          :message-state="messageState"
        >
          <div class="max-w-full flex flex-col items-end">
            <div
              ref="messageContainerRef"
              class="rounded-2xl py-2 px-3 w-fit max-w-full relative"
              :class="[
                {
                  'rounded-br-none':
                    isMyLastSentMessage && !messageState.message.deletedAt,
                  'rounded-bl-none':
                    isMyLastReceivedMessage && !messageState.message.deletedAt,
                  [`${bgPrimary} ${textSecondary} ${borderBlue}`]:
                    messageState.message.deletedAt,
                  [`${bgBlue} text-label-on-accent-primary`]:
                    isMyMessage && !messageState.message.deletedAt,
                  [`${bgSecondary} ${textPrimary}`]:
                    !isMyMessage && !messageState.message.deletedAt,
                  [`${bgRed}`]: isBeingEdited,
                  [`${bgOrange}`]: isHighlighted,
                },
              ]"
              @click="toggleShowFullDate"
            >
              <div
                class="mb-1 flex text-xs w-full relative font-semibold leading-none"
                :class="[
                  isMyMessage && !messageState.message.deletedAt
                    ? 'text-label-on-accent-secondary'
                    : textSecondary,
                  isMyMessage ? 'justify-end' : 'justify-between',
                ]"
              >
                <MessageForwardContent
                  v-if="
                    !messageState.message.deletedAt
                      && messageState.message.forwardedFromId
                  "
                  :forwarded-by="
                    !isMyMessage
                      ? formatName(messageState.message.sentBy)
                      : null
                  "
                />
                <HoverableUserName
                  v-else-if="!isMyMessage"
                  class="overflow-hidden text-ellipsis shrink max-w-32 whitespace-nowrap mr-2"
                  :user="messageState.message.sentBy"
                />

                <span v-if="messageState.message.isEdited" class="italic">
                  {{ t('message.editedMessage') }}&nbsp;
                </span>
                <span>{{ d(messageState.message.createdAt, 'time') }}</span>
              </div>

              <MessageQuoteContent
                v-if="
                  !messageState.message.deletedAt
                    && messageState.message.quotedFrom
                "
                :message="messageState.message.quotedFrom"
                :mine="isMyMessage"
                @click="
                  handleQuotedMessageClick(messageState.message.quotedFrom)
                "
              />

              <MessageAttachments
                v-if="
                  messageState.message?.attachments?.length
                    && messageState.message.attachments.length > 0
                "
                :attachments="messageState.message.attachments"
                :is-my-message="isMyMessage"
                :conversation-id="messageState.message.conversationId"
                :message-id="messageState.message.id"
              />

              <div
                class="overflow-hidden"
                :inert="isLongMessage && !isMessageExpanded"
                :class="{
                  'message-content-clipped':
                    isLongMessage && !isMessageExpanded,
                }"
                :style="{
                  maxHeight:
                    isLongMessage && !isMessageExpanded
                      ? `${COLLAPSED_MESSAGE_HEIGHT_PX}px`
                      : undefined,
                }"
              >
                <div ref="messageContentRef">
                  <MessageContent
                    :message-state="messageState"
                    :is-mine="isMyMessage"
                    :operation-id="props.operationId"
                  />
                </div>
              </div>

              <div
                v-if="isLongMessage"
                class="flex justify-center mt-1"
                @click.stop
              >
                <GrammarQuaternaryButton
                  size="small"
                  :class="{
                    'message-expand-button-on-accent':
                      isMyMessage && !messageState.message.deletedAt,
                  }"
                  :label="
                    isMessageExpanded
                      ? t('message.showLess')
                      : t('message.showEntireMessage')
                  "
                  @click="toggleMessageExpanded"
                />
              </div>
            </div>

            <div
              class="flex w-fit"
              style="z-index: 1"
              :class="{
                'flex-row-reverse': !isMyMessage,
              }"
            >
              <MessageReactions
                :message-state="messageState"
                :max-reactions="maxReactions"
              />

              <transition name="fade">
                <p
                  v-if="isNewest"
                  class="text-xs ml-[0.4rem] flex"
                  :class="[
                    messageState.status === 'error' ? textRed : textSecondary,
                  ]"
                >
                  <template v-if="messageState.status === 'sending'">
                    {{ t('conversation.messageSending') }}
                  </template>
                  <template v-else-if="messageState.status === 'error'">
                    <span class="mi md-16 error" />
                    {{ t('conversation.messageError') }}
                  </template>
                  <template v-else-if="messageState.status === 'sent'">
                    {{ t('conversation.messageSent') }}
                  </template>
                </p>
              </transition>
            </div>
          </div>
        </MessageInteractionHandler>
      </div>

      <div
        class="self-end flex"
        :class="[
          textTertiary,
          !messageState.message.deletedAt ? 'flex' : 'hidden',
          {
            'mb-4':
              messageState.status || messageState?.message?.reactions?.length,
          },
        ]"
      >
        <SeenBy
          :message-state="messageState"
          @show-seen-by-modal="() => toggleSeenByModal(true)"
        />
      </div>
    </div>
    <MessageForward :message-state="messageState" />
    <SeenByModal @close="() => toggleSeenByModal(false)" />
  </div>
</template>

<style scoped>
.message-content-clipped {
  mask-image: linear-gradient(to bottom, black calc(100% - 3rem), transparent);
  -webkit-mask-image:
    linear-gradient(to bottom, black calc(100% - 3rem), transparent);
}

.message-expand-button-on-accent {
  --buttonQuaternaryColorForegroundRegularEnabled:
    var(--label-on-accent-primary);
  --buttonQuaternaryColorForegroundRegularHover:
    var(--label-on-accent-primary);
  --buttonQuaternaryColorForegroundRegularPressed:
    var(--label-on-accent-primary);
}
</style>
